/**
 * assets/versatility-carousel.js
 * Pages the Versatility Carousel a full screen of cards at a time and keeps its dots in sync.
 * Dot clicks jump to a page; the flanking arrows step one page and wrap at either end.
 * Mouse users can also drag the track, and it is touch-draggable from the native scroller.
 * The number of cards per page is measured from the layout, so it follows the CSS breakpoints
 * (2-up above 768px, 1-up below) without duplicating them here. One instance per section.
 */
(function () {
  var SECTION_SELECTOR = '.versatility-carousel';
  var NO_SNAP_CLASS = 'versatility-carousel__card--no-snap';
  var DRAGGING_CLASS = 'versatility-carousel__track--dragging';
  var DRAG_THRESHOLD = 5;

  function initCarousel(root) {
    if (!root || root.dataset.vcInit === 'true') return;

    var track = root.querySelector('.versatility-carousel__track');
    var controls = root.querySelector('.versatility-carousel__controls');
    var prevButton = root.querySelector('[data-vc-prev]');
    var nextButton = root.querySelector('[data-vc-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('.versatility-carousel__dot'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('.versatility-carousel__card'));
    if (!track || !cards.length) return;

    root.dataset.vcInit = 'true';

    var perPage = 1;
    var pageCount = cards.length;

    var trackMetrics = function () {
      var style = window.getComputedStyle(track);
      return {
        paddingLeft: parseFloat(style.paddingLeft) || 0,
        gap: parseFloat(style.columnGap || style.gap) || 0
      };
    };

    // Scroll position that lines a card up with the track's left content edge.
    var cardOffset = function (card) {
      return card.offsetLeft - track.offsetLeft - trackMetrics().paddingLeft;
    };

    // How many whole cards fit across the track at the current viewport.
    var measurePages = function () {
      var metrics = trackMetrics();
      var style = window.getComputedStyle(track);
      var content =
        track.clientWidth - metrics.paddingLeft - (parseFloat(style.paddingRight) || 0);
      var cardWidth = cards[0].getBoundingClientRect().width;
      if (!cardWidth) return;

      perPage = Math.max(1, Math.round((content + metrics.gap) / (cardWidth + metrics.gap)));
      pageCount = Math.ceil(cards.length / perPage);

      // Snap on page boundaries only, so dragging also advances a full page.
      cards.forEach(function (card, i) {
        card.classList.toggle(NO_SNAP_CLASS, i % perPage !== 0);
      });

      // Markup carries one dot per card — the most a page can ever be worth. Drop the surplus.
      dots.forEach(function (dot, i) {
        dot.hidden = i >= pageCount;
        dot.setAttribute('aria-label', i + 1 + ' / ' + pageCount);
      });

      // Nothing to page to when every card is already on screen.
      if (controls) controls.hidden = pageCount < 2;
    };

    var setActive = function (page) {
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === page ? 'true' : 'false');
      });
    };

    // The page whose first card sits closest to the current scroll origin is the active one.
    // The last page's offset can be past the maximum scroll when a page holds several cards,
    // so reaching the end always resolves to the last dot.
    var activePage = function () {
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll > 0 && track.scrollLeft >= maxScroll - 2) return pageCount - 1;

      var best = 0;
      var bestDistance = Infinity;

      for (var page = 0; page < pageCount; page++) {
        var card = cards[page * perPage];
        if (!card) break;
        var distance = Math.abs(cardOffset(card) - track.scrollLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = page;
        }
      }

      return best;
    };

    var refresh = function () {
      measurePages();
      setActive(activePage());
    };

    var frame = null;
    track.addEventListener(
      'scroll',
      function () {
        if (frame) return;
        frame = window.requestAnimationFrame(function () {
          frame = null;
          setActive(activePage());
        });
      },
      { passive: true }
    );

    var goToPage = function (page) {
      var card = cards[page * perPage];
      if (!card) return;
      track.scrollTo({ left: cardOffset(card), behavior: 'smooth' });
    };

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goToPage(i);
      });
    });

    // The arrows wrap, so both stay usable on the first and last page as the design shows.
    var step = function (delta) {
      if (pageCount < 2) return;
      goToPage((activePage() + delta + pageCount) % pageCount);
    };

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        step(-1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        step(1);
      });
    }

    // MOUSE DRAG
    // Touch already drags the native scroller, so this only covers pointers that don't:
    // press, move the scroller by hand, then settle on the nearest page like the arrows do.
    // Snapping is suspended mid-drag so the track follows the cursor instead of fighting it.
    //
    // A press is only ever a drag while the button is down and pulling sideways. Pulling down
    // is the page being scrolled, so the press is dropped and the gesture left to the page.
    var pointerId = null;
    var startX = 0;
    var startY = 0;
    var startScroll = 0;
    var dragging = false;
    var swallowClick = false;

    // Hand the pointer back and forget the press. Safe at any point in a drag, and the only
    // way pointerId is ever cleared — a press left on the books would both block the next
    // pointerdown and make the next bare hover read as a drag against a stale origin.
    var releasePointer = function () {
      if (pointerId !== null && track.hasPointerCapture && track.hasPointerCapture(pointerId)) {
        track.releasePointerCapture(pointerId);
      }

      pointerId = null;
      dragging = false;
    };

    var endDrag = function () {
      if (pointerId === null) return;

      if (dragging) {
        track.classList.remove(DRAGGING_CLASS);
        track.style.removeProperty('scroll-snap-type');
        goToPage(activePage());
        swallowClick = true;
      }

      releasePointer();
    };

    track.addEventListener('pointerdown', function (event) {
      // A stale press must never block a fresh one, so clear it before anything else.
      if (pointerId !== null) endDrag();
      // A drag that ended without a following click must not eat this one.
      swallowClick = false;
      if (event.pointerType === 'touch' || event.button !== 0) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScroll = track.scrollLeft;

      // Captured up front rather than once the drag starts, so the release is always heard
      // here: a press that wanders off the track before it counts as a drag would otherwise
      // report its pointerup to whatever it landed on and leave this one hanging.
      if (track.setPointerCapture) track.setPointerCapture(pointerId);
    });

    track.addEventListener('pointermove', function (event) {
      if (event.pointerId !== pointerId) return;

      // The button can come back up somewhere we never hear about — a native drag taking
      // over, a release outside the window. Treat the first buttonless move as the release.
      if (event.buttons === 0) {
        endDrag();
        return;
      }

      var deltaX = event.clientX - startX;
      var deltaY = event.clientY - startY;

      if (!dragging) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;
        // Whichever way the press broke first wins the gesture. Downward belongs to the page.
        if (Math.abs(deltaY) >= Math.abs(deltaX)) {
          releasePointer();
          return;
        }

        dragging = true;
        track.classList.add(DRAGGING_CLASS);
        track.style.setProperty('scroll-snap-type', 'none');
      }

      event.preventDefault();
      track.scrollLeft = startScroll - deltaX;
    });

    // Capture means these reach the track wherever the pointer ends up, but they are bound on
    // the window so a release still lands if the capture was refused or lost.
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', function (event) {
      if (event.pointerId === pointerId) endDrag();
    });

    // The click that follows a drag lands on whatever card the cursor settled over — eat it.
    track.addEventListener(
      'click',
      function (event) {
        if (!swallowClick) return;
        swallowClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Without this the browser's native image drag takes over part-way through a swipe.
    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    window.addEventListener('resize', refresh);

    refresh();
  }

  function initAll(scope) {
    var containers = (scope || document).querySelectorAll(SECTION_SELECTOR);
    Array.prototype.forEach.call(containers, initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Re-init when the section is re-rendered by the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
